import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  FilterByQuery,
  PaginationQuery,
  PatientIdentifier,
  ReferrerZorgmailRecipient,
  Response,
  TreatmentDocumentIdentifier,
  TreatmentIdentifier,
  ZorgmailMessage,
  ZorgmailSendFormat,
  ZorgmailSendPayload,
  ZorgmailSendPreview,
} from "~/types/api";
import type {
  HandleZorgmailMessagePayload,
  LinkZorgmailMessagePayload,
  ZorgmailInboxMessage,
  ZorgmailInboxRow,
  ZorgmailMessageDetail,
  ZorgmailUnlinkedCount,
} from "~/types/zorgmail";

const recipientParams = (recipient: ReferrerZorgmailRecipient | null) => {
  if (!recipient) return {};

  return {
    "recipient[address]": recipient.address,
    ...(recipient.mailbox_id ? { "recipient[mailbox_id]": recipient.mailbox_id } : {}),
    "recipient[display_name]": recipient.display_name,
  };
};

export function createZorgmailApi({ getRequest, getPaginationRequest, postRequest, deleteRequest }: ApiRequestHelpers) {
  const treatmentDocumentZorgmailPreview = (
    treatmentId: TreatmentIdentifier,
    documentId: TreatmentDocumentIdentifier,
    recipient: ReferrerZorgmailRecipient | null,
    format: ZorgmailSendFormat,
  ) =>
    getRequest<Response<ZorgmailSendPreview>>(`/treatments/${treatmentId}/documents/${documentId}/zorgmail-preview`, {
      params: { ...recipientParams(recipient), format },
      suppressValidationToast: true,
    });

  const sendTreatmentDocumentZorgmail = (
    treatmentId: TreatmentIdentifier,
    documentId: TreatmentDocumentIdentifier,
    payload: ZorgmailSendPayload,
  ) =>
    postRequest<Response<ZorgmailMessage>>(
      `/treatments/${treatmentId}/documents/${documentId}/zorgmail-messages`,
      payload,
      { suppressValidationToast: true },
    );

  const patientZorgmailMessages = (patientId: PatientIdentifier) =>
    getRequest<Response<ZorgmailMessage[]>>(`/patients/${patientId}/zorgmail-messages`);

  const patientZorgmailMessage = (patientId: PatientIdentifier, messageId: string, refresh = false) => {
    const url = `/patients/${patientId}/zorgmail-messages/${messageId}`;

    return refresh
      ? getRequest<Response<ZorgmailMessageDetail>>(url, { params: { refresh: 1 } })
      : getRequest<Response<ZorgmailMessageDetail>>(url);
  };

  const downloadPatientZorgmailMessage = (patientId: PatientIdentifier, messageId: string, preview = false) =>
    getRequest<Blob>(`/patients/${patientId}/zorgmail-messages/${messageId}/download`, {
      ...(preview ? { params: { preview: 1 } } : {}),
      responseType: "blob",
    });

  const zorgmailInboxMessages = (pagination?: PaginationQuery, filterBy?: FilterByQuery) =>
    getPaginationRequest<ZorgmailInboxRow>("/zorgmail-messages", pagination, undefined, undefined, filterBy);

  const zorgmailInboxMessage = (messageId: string) =>
    getRequest<Response<ZorgmailInboxMessage>>(`/zorgmail-messages/${messageId}`);

  const unlinkedZorgmailMessageCount = () => getRequest<Response<ZorgmailUnlinkedCount>>("/zorgmail-messages/count");

  const downloadZorgmailInboxMessage = (messageId: string, preview = false) =>
    getRequest<Blob>(`/zorgmail-messages/${messageId}/download`, {
      ...(preview ? { params: { preview: 1 } } : {}),
      responseType: "blob",
    });

  const linkZorgmailMessageToPatient = (messageId: string, payload: LinkZorgmailMessagePayload) =>
    postRequest<Response<ZorgmailMessage>>(`/zorgmail-messages/${messageId}/link-patient`, payload, {
      suppressValidationToast: true,
    });

  const unlinkZorgmailMessageFromPatient = (messageId: string) =>
    deleteRequest<Response<ZorgmailMessage>>(`/zorgmail-messages/${messageId}/link-patient`, {
      suppressValidationToast: true,
    });

  const markZorgmailMessageHandled = (messageId: string, payload: HandleZorgmailMessagePayload) =>
    postRequest<Response<ZorgmailMessage>>(`/zorgmail-messages/${messageId}/handled`, payload, {
      suppressValidationToast: true,
    });

  const reopenZorgmailMessage = (messageId: string) =>
    deleteRequest<Response<ZorgmailMessage>>(`/zorgmail-messages/${messageId}/handled`, {
      suppressValidationToast: true,
    });

  return {
    treatmentDocumentZorgmailPreview,
    sendTreatmentDocumentZorgmail,
    patientZorgmailMessages,
    patientZorgmailMessage,
    downloadPatientZorgmailMessage,
    zorgmailInboxMessages,
    zorgmailInboxMessage,
    unlinkedZorgmailMessageCount,
    downloadZorgmailInboxMessage,
    linkZorgmailMessageToPatient,
    unlinkZorgmailMessageFromPatient,
    markZorgmailMessageHandled,
    reopenZorgmailMessage,
  };
}

export type ZorgmailApi = ReturnType<typeof createZorgmailApi>;
